import type { SkillDef } from "../types";
import { digits, fmt, isqrt, prob } from "../util";

const SPEC: Record<number, ["p" | "e", number, number]> = {
  1: ["p", 2, 12], 2: ["p", 13, 25], 3: ["p", 26, 50], 4: ["p", 51, 99], 5: ["e", 10, 99],
  6: ["e", 101, 399], 7: ["p", 100, 300], 8: ["e", 400, 2000], 9: ["p", 301, 999], 10: ["e", 2000, 9999],
};
const ENDS: Record<number, number[]> = { 0: [0], 1: [1, 9], 4: [2, 8], 5: [5], 6: [4, 6], 9: [3, 7] };

export const sqrt: SkillDef = {
  key: "sqrt", name: "Square roots", blurb: "exact and estimated", start: 1,
  generate(L, r) {
    const [kind, lo, hi] = SPEC[L];
    if (kind === "p") {
      const root = r.int(lo, hi), n = root * root, k = isqrt(Math.floor(n / 100));
      const cands = ENDS[n % 10].map((e) => 10 * k + e);
      let hint = `Drop the last two digits: ${fmt(Math.floor(n / 100))}. Largest square at or below it is ${k}², so the root starts ${k || ""}_.\n`;
      hint += `Ends in ${n % 10} → ${cands.join(" or ")}.`;
      if (cands.length === 2) {
        const m = 10 * k + 5;
        hint += ` ${m}² = ${fmt(m * m)}, so it's the ${n > m * m ? "larger" : "smaller"}: ${root}`;
      }
      return prob(`√${fmt(n)}`, root, hint, { t: root <= 12 ? 2.5 : root < 100 ? 6 : 10 });
    }
    let n: number;
    do { n = r.int(lo, hi); } while (isqrt(n) ** 2 === n);
    const s = isqrt(n), c = n - s * s <= (s + 1) ** 2 - n ? s : s + 1;
    const approx = c + (n - c * c) / (2 * c), exact = Math.sqrt(n);
    return prob(`√${fmt(n)}`, Math.round(exact * 10) / 10,
      `Nearest square: ${c}² = ${fmt(c * c)}.\n√${fmt(n)} ≈ ${c} + (${fmt(n)} − ${fmt(c * c)}) ÷ (2×${c}) = ${approx.toFixed(2)}  (actual ${exact.toFixed(3)})`,
      { tol: 0.1, note: "to 1 decimal", t: 8 + 1.5 * digits(n) });
  },
};
